<template>
  <div class="container-wrapper my-4 pt-sm-3 pb-5">
    <h4 class="text-center mb-4 pb-2">{{ $t('message.buildAndPrice.modelSelect.heading') }}</h4>
    <div class="row form-row" v-if="selectedModelYears && formattedModels">
      <div v-for="(item, propertyName, index) in formattedModels" :key="`card-${index}`" class="col-sm-4 mb-sm-2">
        <div
          class="vehicle-model"
          :class="{ active: selectedModelName === propertyName, 'top-border-mobile': index !== 0 }"
          @click="selectModel(selectedModelYears[propertyName])"
          v-on:keyup.enter="selectModel(selectedModelYears[propertyName])"
          :aria-pressed="selectedModelName === propertyName"
          :aria-label="`${$t('message.buildAndPrice.modelSelect.select')} ${getSelectedModelYear(
            propertyName
          )} ${propertyName}`"
          tabindex="0"
          role="button"
        >
          <div class="row align-items-center form-row">
            <!-- vehicle image -->
            <div class="col-6 col-sm-12">
              <div
                class="px-4 py-2 py-sm-0 pt-sm-2 model-image"
                :class="{ active: selectedModelYears[item.name] === item.id }"
                v-for="(item, index) in item"
                :key="`image-${index}`"
              >
                <img
                  :alt="`${item.year} ${item.make} ${item.name}`"
                  :src="item.image || require('@/assets/vehicle-fallback.png')"
                />
              </div>
            </div>
            <!-- vehicle details -->
            <div class="col-6 col-sm-12">
              <div class="px-2 py-4 px-sm-3 pt-sm-0 pb-sm-3">
                <div>
                  <!-- vehicle years -->
                  <div
                    v-for="(item, index) in item"
                    :key="`year-${index}`"
                    class="model-year font-weight-bold"
                    :class="{ active: selectedModelYears[item.name] === item.id }"
                    v-on:keyup.enter.stop.prevent="selectModelYear(item.name, item.id)"
                    @click.stop.prevent="selectModelYear(item.name, item.id)"
                    role="button"
                    :tabindex="selectedModelYears[item.name] === item.id ? 'auto' : 0"
                    :aria-pressed="selectedModelYears[item.name] === item.id"
                    :aria-label="`${$t('message.buildAndPrice.modelSelect.view')} ${item.year} ${item.make} ${
                      item.name
                    }`"
                  >
                    {{ item.year }}
                  </div>
                </div>
                <!-- vehicle name and price -->
                <div
                  v-for="(item, index) in item"
                  :key="`details-${index}`"
                  class="model-details"
                  :class="{ active: selectedModelYears[item.name] === item.id }"
                >
                  <div class="font-weight-bold mb-2">{{ `${item.make} ${item.name}` }}</div>
                  <div class="p-small m-0">
                    {{
                      $t('message.buildAndPrice.modelSelect.startingAt', {
                        price: $n(item.startingAtPrice, 'currencyDollars', languageCode),
                      })
                    }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="checkmark-icon primary-fill-svg">
            <svg xmlns="http://www.w3.org/2000/svg" class="info" width="20" height="20" viewBox="0 0 20 20">
              <path
                fill="#007bc7"
                fill-rule="evenodd"
                d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0zm-8.925 4.236L15.01 8.3a.516.516 0 0 0 0-.73l-.73-.73a.516.516 0 0 0-.73 0l-4.84 4.841-2.26-2.26a.516.516 0 0 0-.73 0l-.73.73a.516.516 0 0 0 0 .73l3.355 3.355a.516.516 0 0 0 .73 0z"
              ></path>
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

export default {
  name: 'ModelSelect',
  computed: {
    ...mapState('buildAndPrice', ['models', 'formattedModels', 'selectedModelYears', 'selectedModelId']),
    ...mapGetters({
      languageCode: 'user/getLanguageCodeForNumericalInternationalization',
      selectedModel: 'buildAndPrice/selectedModel',
    }),
    selectedModelName() {
      return this.selectedModel ? this.selectedModel.name : null;
    },
  },
  async created() {
    if (!this.models) {
      await this.getModels();
    }
  },
  methods: {
    // Initial state should have the first model year selected as default
    async setInitialActiveYears() {
      const selectedModelYears = {};
      const array = Object.keys(this.formattedModels);
      array.forEach(item => {
        selectedModelYears[`${item}`] = this.formattedModels[`${item}`][0].id;
      });
      this.$store.commit('buildAndPrice/SET_STATE', ['selectedModelYears', selectedModelYears]);
    },
    async formatModels() {
      // Order by year and name
      const formattedModels = this.models.sort((a, b) => b.year - a.year || a.name.localeCompare(b.name));
      // Group by names
      const grouped = formattedModels.reduce((result, currentValue) => {
        // eslint-disable-next-line no-param-reassign
        (result[currentValue.name] = result[currentValue.name] || []).push(currentValue);
        return result;
      }, {});
      this.$store.commit('buildAndPrice/SET_STATE', ['formattedModels', grouped]);
    },
    async selectModelYear(name, value) {
      this.$store.commit('buildAndPrice/SET_SELECTED_MODEL_YEAR', [name, value]);
      // If we are selecting a different year of the already selected model
      // the new year should be selected, but no navigation
      if (name === this.selectedModelName && value !== this.selectedModelId) {
        this.$store.commit('buildAndPrice/SET_STATE', ['selectedModelId', value]);
        this.$store.commit('buildAndPrice/RESET_SELECTED_OPTIONS');
        this.$store.commit('buildAndPrice/RESET_TRIM');
        try {
          await this.$store.dispatch('buildAndPrice/getBuildAndPriceTrims', value);
        } catch (e) {
          this.$log('Error getBuildAndPriceTrims', e);
          this.$router.push('error');
        }
      }
    },
    async getModels() {
      try {
        this.$store.commit('SET_LOADING', true);
        await this.$store.dispatch('buildAndPrice/getBuildAndPriceModels');
        await this.formatModels();
        await this.setInitialActiveYears();
      } catch (e) {
        this.$router.push('error');
        this.$log('Error getting B&P vehicle models', e);
      } finally {
        this.$store.commit('SET_LOADING', false);
      }
    },
    getSelectedModelYear(name) {
      const modelId = this.selectedModelYears[name];
      const model = this.models.filter(item => item.id === modelId)[0];
      return model.year;
    },
    async selectModel(value) {
      try {
        if (value !== this.selectedModelId) {
          this.$store.commit('SET_LOADING', true);
          this.$store.commit('buildAndPrice/SET_STATE', ['selectedModelId', value]);
          this.$store.commit('buildAndPrice/RESET_SELECTED_OPTIONS');
          this.$store.commit('buildAndPrice/RESET_TRIM');
          await this.$store.dispatch('buildAndPrice/getBuildAndPriceTrims', value);
        }
        this.$router.push('trim-select');
      } catch (e) {
        this.$router.push('error');
        this.$log('Error selecting B&P vehicle model', e);
      } finally {
        this.$store.commit('SET_LOADING', false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/variables';

.vehicle-model {
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 100ms linear;
  @media (max-width: 575px) {
    &.top-border-mobile {
      border-top: 1px solid #dae2ea;
    }
  }
  &:hover {
    border-radius: 6px;
    background-color: $primary-light-background;
    border: 1px solid #dae2ea;
  }
  &.active {
    border-radius: 6px;
    background-color: $primary-light-background;
    border: 1px solid $primary;
    .checkmark-icon {
      display: block;
    }
  }
  &:focus-visible,
  &:focus {
    outline: none;
    border: 1px solid $primary;
  }
  .model-image {
    display: none;
    img {
      max-width: 100%;
    }
    &.active {
      display: block;
    }
  }
  .model-year {
    color: #6d7783;
    padding: 1px 5px 3px 0;
    margin-right: 10px;
    position: relative;
    display: inline-block;
    &:hover {
      color: $primary;
    }
    &.active {
      color: #19222b;
    }
    &:focus-visible,
    &:focus {
      outline: none;
      color: $primary;
    }
    &:nth-child(2) {
      margin-left: 8px;
    }
    &:nth-child(2):before {
      content: '';
      height: 15px;
      width: 2px;
      display: inline-block;
      background-color: #6d7783;
      position: absolute;
      top: 6px;
      left: -12px;
    }
  }
  .model-details {
    display: none;
    &.active {
      display: block;
    }
  }
  .checkmark-icon {
    display: none;
    position: absolute;
    right: 12px;
    top: 5px;
  }
}
</style>
